/*
 * SPDX-FileCopyrightText: 2022 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useApplicationState } from '@hooks/common/use-application-state'
import { useBooleanState } from '../../../../hooks/common/use-boolean-state'
import { RevisionModal } from '../../document-bar/revisions/revision-modal'
import { SidebarButton } from '../sidebar-button/sidebar-button'
import type { SpecificSidebarEntryProps } from '../types'
import React, { Fragment, useMemo } from 'react'
import { ClockHistory as IconClockHistory, Journal } from 'react-bootstrap-icons'
import { Trans } from 'react-i18next'
import { ShowIf } from '@components/common/show-if/show-if'

/**
 * Renders a button to open the revision modal for the sidebar.
 *
 * @param className Additional classes directly given to the button
 * @param hide If the button should be hidden
 */
export const RevisionSidebarEntry: React.FC<SpecificSidebarEntryProps & {
  showVersionHistory: boolean;
}> = ({ className, hide, showVersionHistory }) => {
  const [modalVisibility, showModal, closeModal] = useBooleanState()
  const [draftModalVisibilty, showDraftModal, closeDraftModal] = useBooleanState()
  const { draftStatus, draftContent, publishedAt } = useApplicationState((state) => ({
    draftStatus: state.noteDraft.status,
    draftContent: state.noteDraft.content,
    publishedAt: state.noteDetails.publishedAt
  }));

  const isArticlePublished = useMemo(() => publishedAt !== null, [publishedAt]);

  return (
    <Fragment>
      <ShowIf condition={showVersionHistory}>
        <SidebarButton
          hide={hide}
          className={className}
          icon={IconClockHistory}
          onClick={showModal}>
          <Trans i18nKey={'editor.modal.revision.title'} />
        </SidebarButton>
      </ShowIf>
      <ShowIf condition={draftContent !== null && draftStatus !== 'approved' && isArticlePublished}>
        <SidebarButton
          hide={hide}
          className={className}
          icon={Journal}
          onClick={showDraftModal}>
          <Trans i18nKey={'editor.modal.draft.title'} />
        </SidebarButton>
      </ShowIf>
      <RevisionModal
        isDraft={draftModalVisibilty}
        show={modalVisibility || draftModalVisibilty}
        onHide={modalVisibility ? closeModal : closeDraftModal}
      />
    </Fragment>
  );
}
